import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAnalysisStatus } from '@/services/scriptService';
import {
  clearInflightAnalysis,
  getInflightAnalysis,
  type InflightAnalysisRecord,
} from '@/utils/inflightAnalysisStorage';

const WORKFLOW_SESSION_KEY = 'workflow_active_session_id';

export function useInflightAnalysis(onFinished?: () => void) {
  const navigate = useNavigate();
  const [inflight, setInflight] = useState<InflightAnalysisRecord | null>(() =>
    getInflightAnalysis(),
  );

  const checkStatus = useCallback(async () => {
    const record = getInflightAnalysis();
    if (!record?.analysisSessionId) {
      setInflight(null);
      return;
    }
    try {
      const status = await getAnalysisStatus(record.analysisSessionId);
      if (status.status === 'completed' || status.status === 'error') {
        clearInflightAnalysis();
        setInflight(null);
        onFinished?.();
        return;
      }
      setInflight(record);
    } catch {
      setInflight(record);
    }
  }, [onFinished]);

  useEffect(() => {
    void checkStatus();
    const id = setInterval(() => void checkStatus(), 5000);
    return () => clearInterval(id);
  }, [checkStatus]);

  const openWorkflow = useCallback(() => {
    const record = getInflightAnalysis();
    if (!record) return;
    const sessionId = record.workflowSessionId || record.analysisSessionId;
    sessionStorage.setItem(WORKFLOW_SESSION_KEY, sessionId);
    navigate('/workflow');
  }, [navigate]);

  return { inflight, openWorkflow, refresh: checkStatus };
}
